import { useEffect, useState } from 'react';
import { Truck, WarningCircle } from 'shared/icons';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Modal from '../../ui/Modal';
import { MONO_CLASS } from '../../ui/Mono';
import Select from '../../ui/Select';
import Textarea from '../../ui/Textarea';
import { ExtractListRows } from '../payload';
import { FormatOutboundSummary, ParseLpnCodeList } from './shipmentModel';
import { CreateShipment, ListDockLocations, ListOutbounds } from './shippingApi';

/**
 * ABRIR O EMBARQUE (§6.8, passo 1) — a sessão de carregamento de um ROMANEIO
 * QUE JÁ EXISTE.
 *
 * Nada aqui cria romaneio, transportadora, motorista, placa ou PDF: §7 é
 * explícito em "reutilize/adapte, não construa um segundo módulo de romaneio".
 * O que o embarque acrescenta é o que o romaneio nunca soube — o registro do
 * que entrou fisicamente no veículo.
 *
 * ── A DOCA aqui é ONDE O CAMINHÃO ESTÁ, não uma etiqueta a bipar (13/08) ────
 * Ela continua sendo escolhida na abertura porque é por ela que o operador
 * reconhece o embarque dele na lista do coletor. O que morreu foi o SEGUNDO
 * bipe no carregamento: estar na doca voltou a ser a posição do pallet, que a
 * movimentação do M4/M5 já registra.
 *
 * ── Duas permissões que NÃO vêm junto com `wms:manage:shipping` ─────────────
 * `GET /wms/outbounds` exige `wms:read` e `GET /warehouse/locations` exige
 * `wms:manage:locations`. Uma conta só de expedição recebe 403 nas duas e
 * ficaria sem seletor nenhum. Em vez de esconder a tela, cada seletor DEGRADA
 * para o campo de texto equivalente — que é exatamente o que o backend valida
 * de qualquer forma (código da doca, id do romaneio). A tela diz o porquê;
 * não finge que a lista está vazia.
 */
const OpenShipmentModal = ({ isOpen, onClose, onCreated, label }) => {
    const [outbounds, setOutbounds] = useState([]);
    const [outboundsForbidden, setOutboundsForbidden] = useState(false);
    const [docks, setDocks] = useState([]);
    const [docksForbidden, setDocksForbidden] = useState(false);
    const [outboundId, setOutboundId] = useState('');
    const [dockCode, setDockCode] = useState('');
    const [lpnText, setLpnText] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        let alive = true;
        setErrorMessage(null);

        ListOutbounds({ limit: 200 })
            .then((data) => {
                if (!alive) {
                    return;
                }
                // Romaneio COLETADO ou REJEITADO não recebe mais carga — o
                // backend recusa com 409, então nem entra na lista.
                setOutbounds(
                    ExtractListRows(data, ['outbounds']).filter(
                        (row) => !['collected', 'rejected'].includes(String(row?.status || '')),
                    ),
                );
                setOutboundsForbidden(false);
            })
            .catch((error) => {
                if (alive) {
                    setOutbounds([]);
                    setOutboundsForbidden(error?.status === 403);
                }
            });

        ListDockLocations()
            .then((data) => {
                if (alive) {
                    setDocks(ExtractListRows(data, ['locations']));
                    setDocksForbidden(false);
                }
            })
            .catch((error) => {
                if (alive) {
                    setDocks([]);
                    setDocksForbidden(error?.status === 403);
                }
            });

        return () => {
            alive = false;
        };
    }, [isOpen]);

    const reset = () => {
        setOutboundId('');
        setDockCode('');
        setLpnText('');
        setNotes('');
        setErrorMessage(null);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const outbound = outboundId.trim();
        const dock = dockCode.trim().toUpperCase();
        if (!outbound || !dock) {
            setErrorMessage(label('wms.common.required'));
            return;
        }

        setSubmitting(true);
        setErrorMessage(null);
        try {
            const created = await CreateShipment({
                outbound_id: outbound,
                dock_location_code: dock,
                lpn_codes: ParseLpnCodeList(lpnText),
                notes: notes.trim() || null,
            });
            reset();
            onCreated?.(created);
        } catch (error) {
            // A mensagem do backend já explica o caso (romaneio com embarque
            // vivo, romaneio coletado, pallet em outro embarque). Reescrevê-la
            // aqui criaria uma segunda versão da regra.
            setErrorMessage(error?.message || label('wms.common.load_error'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={submitting ? () => {} : onClose}
            title={label('wms.shipment.open_title')}
            icon={<Truck size={22} />}
            size="lg"
            actions={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={submitting}>
                        {label('wms.common.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} loading={submitting}>
                        {label('wms.shipment.open_confirm')}
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.shipment.open_hint')}</p>

                {outboundsForbidden ? (
                    <div className="space-y-2">
                        <p className="flex items-start gap-2 rounded-lg bg-info-soft px-3 py-2 text-sm text-info-text dark:bg-info-900/30 dark:text-info-200">
                            <WarningCircle size={16} className="mt-0.5 shrink-0" />
                            {label('wms.shipment.outbound_forbidden')}
                        </p>
                        <Input
                            label={label('wms.shipment.outbound_id_manual')}
                            value={outboundId}
                            onChange={(event) => setOutboundId(event.target.value)}
                            className={MONO_CLASS}
                            autoComplete="off"
                        />
                    </div>
                ) : (
                    <Select
                        label={label('wms.shipment.outbound_picker')}
                        value={outboundId}
                        onChange={(event) => setOutboundId(event.target.value)}
                    >
                        <option value="">{outbounds.length === 0 ? label('wms.shipment.outbound_empty') : '—'}</option>
                        {outbounds.map((row) => (
                            <option key={row.id} value={row.id}>
                                {FormatOutboundSummary(row) || row.id}
                            </option>
                        ))}
                    </Select>
                )}

                {docksForbidden || docks.length === 0 ? (
                    <div className="space-y-1">
                        <Input
                            label={label('wms.shipment.dock_picker')}
                            value={dockCode}
                            onChange={(event) => setDockCode(event.target.value.toUpperCase())}
                            placeholder="DCA-801"
                            className={MONO_CLASS}
                            autoComplete="off"
                        />
                        {docksForbidden ? (
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.shipment.dock_forbidden')}
                            </p>
                        ) : null}
                    </div>
                ) : (
                    <Select
                        label={label('wms.shipment.dock_picker')}
                        value={dockCode}
                        onChange={(event) => setDockCode(event.target.value)}
                        className={MONO_CLASS}
                    >
                        <option value="">—</option>
                        {docks.map((row) => (
                            <option key={row.id} value={row.code}>
                                {row.code}
                            </option>
                        ))}
                    </Select>
                )}
                {/* ⚠️ O texto de `wms.shipment.dock_hint` foi REMOVIDO daqui em
                    13/08: ele dizia "é esta etiqueta que o operador bipa para
                    confirmar que a carga entrou no veículo", e ninguém bipa mais
                    a doca no carregamento. O dicionário é do dono
                    (`warehouseLabels.js`), então a chave continua lá com o texto
                    velho e está RELATADA — instrução falsa na tela é pior que
                    campo sem dica, e o rótulo "Doca do embarque" já diz o que é. */}

                <div className="space-y-1">
                    <Textarea
                        label={label('wms.shipment.lpn_codes')}
                        value={lpnText}
                        onChange={(event) => setLpnText(event.target.value)}
                        rows={4}
                        placeholder={'REV-260812-001\nPA-260812-004'}
                        className={MONO_CLASS}
                    />
                    <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.shipment.add_lpns_hint')}</p>
                </div>

                <Textarea
                    label={label('wms.shipment.field.notes')}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    rows={2}
                />

                {errorMessage ? (
                    <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-text dark:bg-danger-900/30 dark:text-danger-300">
                        {errorMessage}
                    </p>
                ) : null}
            </form>
        </Modal>
    );
};

export default OpenShipmentModal;
